import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { isToday, parseISO } from 'date-fns';
import { Grid } from '@material-ui/core';

import useSession from 'core/hooks/use-session';
import { Card } from 'core/components/card';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { ApplicationLinks } from './components/application-url-form';
import { Task } from './components/task';
import { InternalTask } from './components/internal-task';
import completedIcon from 'customer/assets/completed.svg';
import clock from 'core/assets/svg/clock-filled.svg';
import coffeeIcon from './assets/coffee.svg';
import { InventoryImages } from './components/inventory-images';
import useStyles from './onboarding-task-center.styles';

const OnboardingTaskCenter = () => {
  const classes = useStyles();
  const { canEditPolicies, canUploadTaskCenterDocuments, canViewInternalTasks, isAgency } = useSession();
  const {
    account,
    account: {
      accountLoading,
      id: accountId,
      fname,
      lname,
      tasks,
      incompleteTasks,
      completedTasks,
      tasksError,
      created,
      internalTasks,
      pendingInternalTasks,
      approvedInternalTasks
    }
  } = useStore();
  const toast = useToast();

  useEffect(() => {
    if (tasksError) {
      toast.notify({
        type: 'error',
        message: tasksError
      });
    }
  }, [tasksError, toast]);

  // Fetch internal tasks separately — only for users with the permission
  useEffect(() => {
    if (canViewInternalTasks && accountId) {
      account.fetchInternalTasks(accountId);
    }
  }, [canViewInternalTasks, accountId, account]);

  const boundToday = created ? isToday(parseISO(created)) : false;

  // Appcues teaching bubble for task statuses (#24215)
  useEffect(() => {
    // Only show for IAs (not agencies) and when there are review pending tasks
    if (!isAgency && completedTasks.length > 0) {
      const hasReviewPendingTasks = completedTasks.some((task) => task.completedDate && !task.approvalDate);

      if (hasReviewPendingTasks) {
        // Show the teaching bubble with low priority (after other bubbles)
        setTimeout(() => {
          window?.Appcues?.show('24215');
        }, 1000); // Small delay to ensure other bubbles show first
      }
    }
  }, [isAgency, completedTasks]);

  // Internal tasks do NOT count toward the submitted tasks count (per Figma spec)
  const hasAnyTasks = tasks.length > 0 || (canViewInternalTasks && internalTasks.length > 0);

  // Summary banner reflects overall task progress (#31628). Regular tasks have no
  // separate "approved" bucket — approval is just `approvalDate` on a completed task.
  const totalTaskCount = tasks.length;
  const submittedTaskCount = completedTasks.length;
  const approvedTaskCount = completedTasks.filter((task) => !!task.approvalDate).length;

  // Icon matches the least-complete status: gray clock unless every task is approved,
  // in which case the green checkmark. Banner only shows once at least one task is submitted.
  let summaryIcon = clock;
  let summaryIconAlt = 'Clock Icon';
  let summaryMessage = null;
  if (submittedTaskCount > 0) {
    if (submittedTaskCount < totalTaskCount) {
      summaryMessage = `${submittedTaskCount}/${totalTaskCount} submitted for ${fname} ${lname}`;
    } else if (approvedTaskCount < totalTaskCount) {
      summaryMessage = `All of ${fname} ${lname}'s tasks have been submitted. Underwriting review in progress`;
    } else if (!canViewInternalTasks || pendingInternalTasks.length === 0) {
      // Suppress the "all completed" line when a permissioned staff user still has
      // pending internal tasks to review — it would otherwise sit above unfinished work.
      summaryIcon = completedIcon;
      summaryIconAlt = 'Rounded checkmark';
      summaryMessage = `All of ${fname} ${lname}'s tasks have been completed`;
    }
  }

  return (
    <Section
      title="Task Center"
      rightLabel="Total"
      rightValue={tasks.length ? `${accountLoading ? '...' : incompleteTasks.length} incomplete tasks` : null}
    >
      {accountLoading ? (
        <Loading />
      ) : (
        <div className={classes.sectionContent}>
          {canEditPolicies && <ApplicationLinks />}
          <InventoryImages />

          {!hasAnyTasks && boundToday && (
            <Card className={classes.container}>
              <Grid container justifyContent="center" alignItems="center" direction="column">
                <img className={classes.icon} src={clock} alt="Clock Icon" />
                <Label type="darkGreenMedium" className={classes.label}>
                  Onboarding tasks are generating
                </Label>
                <Label type="greenExtraSmall">Try refreshing the page to see if they're ready.</Label>
              </Grid>
            </Card>
          )}

          {!hasAnyTasks && !boundToday && (
            <Card>
              <div className={classes.empty}>
                <img className={classes.icon} src={boundToday ? clock : coffeeIcon} alt="Icon" />
                <Label className={classes.greenLabel}>
                  {boundToday
                    ? 'Onboarding tasks generating, please check back soon.'
                    : 'Relax, there are no tasks to complete'}
                </Label>
              </div>
            </Card>
          )}
          {hasAnyTasks && (
            <Card style={{ overflow: 'visible' }}>
              {summaryMessage && (
                <div className={classes.container}>
                  <img src={summaryIcon} alt={summaryIconAlt} />
                  <Label type="darkGreenSmall">{summaryMessage}</Label>
                </div>
              )}
              {incompleteTasks.map((task, ix) => (
                <Task key={ix} {...task} readOnly={!canUploadTaskCenterDocuments} />
              ))}
              <CollapsibleCard
                showChevron={Boolean(completedTasks.length) || (canViewInternalTasks && internalTasks.length > 0)}
                className={classes.collapsible}
                initialOpen={true}
                content={
                  <>
                    <Label type="infoSubtitle" className={classes.greenLabel}>
                      Submitted
                    </Label>
                    <Label type="noteSmall" className={classes.greenLabel}>
                      {completedTasks.length} {completedTasks.length === 1 ? 'task' : 'tasks'} submitted
                    </Label>
                  </>
                }
                type="noBorders"
              >
                {completedTasks.map((task, ix) => (
                  <Task key={ix} {...task} />
                ))}

                {/* Internal tasks rendered inside the Submitted section, after regular tasks */}
                {canViewInternalTasks &&
                  pendingInternalTasks.map((task) => <InternalTask key={task.taskId} {...task} />)}
                {canViewInternalTasks &&
                  approvedInternalTasks.map((task) => <InternalTask key={task.taskId} {...task} />)}
              </CollapsibleCard>
            </Card>
          )}
        </div>
      )}
    </Section>
  );
};

export default observer(OnboardingTaskCenter);
